"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useState } from "react";
import type { ApiWave } from "@/generated/models/ApiWave";
import MyStreamWaveDesktopTabs from "../MyStreamWaveDesktopTabs";
import { useContentTab } from "@/components/brain/ContentTabContext";
import MemesArtSubmissionModal from "@/components/waves/memes/MemesArtSubmissionModal";
import MyStreamWaveTabsMemeSubmit from "./MyStreamWaveTabsMemeSubmit";
import { useWave } from "../../../../hooks/useWave";
import { useDecisionPoints } from "../../../../hooks/waves/useDecisionPoints";
import { Time } from "../../../../helpers/time";
import type { TimeLeft } from "../../../../helpers/waves/time.utils";
import { calculateTimeLeft } from "../../../../helpers/waves/time.utils";
import { CompactTimeCountdown } from "../../../waves/leaderboard/time/CompactTimeCountdown";
import { useSidebarState } from "../../../../hooks/useSidebarState";
import { ChevronDoubleLeftIcon, ArrowLeftIcon, MagnifyingGlassIcon, ShareIcon, LinkIcon, CheckIcon } from "@heroicons/react/24/outline";
import WavePicture from "../../../waves/WavePicture";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { createBreakpoint } from "react-use";
import WaveDropsSearchModal from "@/components/waves/drops/search/WaveDropsSearchModal";
import { MyStreamWaveTab } from "@/types/waves.types";
import { useWaveChatScrollOptional } from "@/contexts/wave/WaveChatScrollContext";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { getWaveHomeRoute } from "@/helpers/navigation.helpers";
import { useWaveShareCopyAction } from "@/hooks/waves/useWaveShareCopyAction";
import WaveDescriptionPopover from "@/components/waves/header/WaveDescriptionPopover";
import { getWaveDescriptionPreviewText } from "@/helpers/waves/waveDescriptionPreview";
const useBreakpoint = createBreakpoint({
  LG: 1024,
  MD: 768,
  S: 0
});
interface MyStreamWaveTabsMemeProps {
  readonly wave: ApiWave;
}
const MyStreamWaveTabsMeme: React.FC<MyStreamWaveTabsMemeProps> = t0 => {
  const $ = _c(100);
  const {
    wave
  } = t0;
  const {
    activeContentTab,
    setActiveContentTab
  } = useContentTab();
  const {
    toggleRightSidebar,
    isRightSidebarOpen
  } = useSidebarState();
  const [isMemesModalOpen, setIsMemesModalOpen] = useState(false);
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const {
    isApp
  } = useDeviceInfo();
  const breakpoint = useBreakpoint();
  const isCompact = breakpoint === "S";
  const showBackButton = breakpoint !== "LG";
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const waveChatScroll = useWaveChatScrollOptional();
  const isDirectMessage = wave.chat.scope.group?.is_direct_message ?? false;
  const showShareAction = !isDirectMessage;
  let t1;
  if ($[0] !== wave) {
    t1 = getWaveDescriptionPreviewText(wave);
    $[0] = wave;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const previewText = t1;
  const showDescriptionPreview = showShareAction && !!previewText;
  let t2;
  if ($[2] !== isDirectMessage || $[3] !== wave.id || $[4] !== wave.name) {
    t2 = {
      waveId: wave.id,
      waveName: wave.name,
      isDirectMessage
    };
    $[2] = isDirectMessage;
    $[3] = wave.id;
    $[4] = wave.name;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    mode: waveLinkActionMode,
    label: waveLinkActionLabel,
    feedbackState: waveLinkActionFeedbackState,
    onClick: handleWaveLinkActionClick
  } = useWaveShareCopyAction(t2);
  const waveLinkActionIconColor = waveLinkActionFeedbackState === "idle" ? "tw-text-iron-200" : "tw-text-emerald-300";
  let t3;
  if ($[6] !== waveLinkActionFeedbackState || $[7] !== waveLinkActionMode) {
    t3 = () => {
      if (waveLinkActionFeedbackState !== "idle") {
        return <CheckIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" />;
      }
      if (waveLinkActionMode === "share") {
        return <ShareIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" />;
      }
      return <LinkIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" />;
    };
    $[6] = waveLinkActionFeedbackState;
    $[7] = waveLinkActionMode;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const renderWaveLinkActionIcon = t3;
  const {
    isMemesWave,
    isRankWave,
    pauses: t4
  } = useWave(wave);
  const {
    filterDecisionsDuringPauses
  } = t4;
  const {
    allDecisions
  } = useDecisionPoints(wave);
  let t5;
  if ($[9] !== allDecisions || $[10] !== filterDecisionsDuringPauses) {
    const decisionsAsApiFormat = allDecisions.map(_temp);
    const filtered = filterDecisionsDuringPauses(decisionsAsApiFormat);
    t5 = allDecisions.filter(decision_0 => filtered.some(f => f.decision_time === decision_0.timestamp));
    $[9] = allDecisions;
    $[10] = filterDecisionsDuringPauses;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const filteredDecisions = t5;
  let t6;
  if ($[12] !== filteredDecisions) {
    t6 = filteredDecisions.find(_temp2)?.timestamp ?? null;
    $[12] = filteredDecisions;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const nextDecisionTime = t6;
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0
    };
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const [timeLeft, setTimeLeft] = useState(t7);
  let t8;
  let t9;
  if ($[15] !== nextDecisionTime) {
    t8 = () => {
      if (typeof nextDecisionTime !== "number") {
        return;
      }
      setTimeLeft(calculateTimeLeft(nextDecisionTime));
      const intervalId = setInterval(() => {
        const newTimeLeft = calculateTimeLeft(nextDecisionTime);
        setTimeLeft(newTimeLeft);
        if (newTimeLeft.days === 0 && newTimeLeft.hours === 0 && newTimeLeft.minutes === 0 && newTimeLeft.seconds === 0) {
          clearInterval(intervalId);
        }
      }, 1000);
      return () => clearInterval(intervalId);
    };
    t9 = [nextDecisionTime];
    $[15] = nextDecisionTime;
    $[16] = t8;
    $[17] = t9;
  } else {
    t8 = $[16];
    t9 = $[17];
  }
  useEffect(t8, t9);
  let t10;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      setIsMemesModalOpen(true);
    };
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const handleMemesSubmit = t10;
  let t11;
  if ($[19] !== isApp || $[20] !== router || $[21] !== searchParams || $[22] !== wave.chat.scope.group?.is_direct_message) {
    t11 = () => {
      const params = new URLSearchParams(searchParams.toString() || "");
      params.delete("wave");
      params.delete("serialNo");
      params.delete("divider");
      params.delete("drop");
      const basePath = getWaveHomeRoute({
        isDirectMessage: wave.chat.scope.group?.is_direct_message ?? false,
        isApp
      });
      const newUrl = params.toString() ? `${basePath}?${params.toString()}` : basePath;
      router.push(newUrl, {
        scroll: false
      });
    };
    $[19] = isApp;
    $[20] = router;
    $[21] = searchParams;
    $[22] = wave.chat.scope.group?.is_direct_message;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const handleMobileBack = t11;
  let t12;
  if ($[24] !== pathname || $[25] !== router || $[26] !== searchParams || $[27] !== setActiveContentTab || $[28] !== wave.id || $[29] !== waveChatScroll) {
    t12 = serialNo => {
      setActiveContentTab(MyStreamWaveTab.CHAT);
      if (waveChatScroll) {
        waveChatScroll.requestScrollToSerialNo({
          waveId: wave.id,
          serialNo
        });
        return;
      }
      const params_0 = new URLSearchParams(searchParams.toString() || "");
      params_0.set("serialNo", String(serialNo));
      router.replace(`${pathname}?${params_0.toString()}`, {
        scroll: false
      });
    };
    $[24] = pathname;
    $[25] = router;
    $[26] = searchParams;
    $[27] = setActiveContentTab;
    $[28] = wave.id;
    $[29] = waveChatScroll;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  const handleSearchSelect = t12;
  let t13;
  if ($[31] !== handleMobileBack || $[32] !== showBackButton) {
    t13 = showBackButton && <button onClick={handleMobileBack} className="-tw-ml-2.5 tw-mr-1.5 tw-flex tw-h-full tw-items-center tw-border-0 tw-bg-transparent tw-p-0 tw-px-2.5 tw-text-iron-300 tw-transition-colors hover:tw-text-iron-50" aria-label="Go back"><ArrowLeftIcon className="tw-h-6 tw-w-6 tw-flex-shrink-0" /></button>;
    $[31] = handleMobileBack;
    $[32] = showBackButton;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  const t14 = wave.name;
  const t15 = wave.picture;
  let t16;
  if ($[34] !== wave.contributors_overview) {
    t16 = wave.contributors_overview.map(_temp3);
    $[34] = wave.contributors_overview;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== t16 || $[37] !== wave.name || $[38] !== wave.picture) {
    t17 = <div className="tw-size-9 tw-flex-shrink-0 tw-rounded-full tw-ring-1 tw-ring-white/30 tw-ring-offset-1 tw-ring-offset-iron-950"><WavePicture name={t14} picture={t15} contributors={t16} /></div>;
    $[36] = t16;
    $[37] = wave.name;
    $[38] = wave.picture;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== previewText || $[41] !== showDescriptionPreview || $[42] !== wave) {
    t18 = showDescriptionPreview ? <WaveDescriptionPopover wave={wave} align="left" ariaLabel="Show wave description" triggerClassName="tw-flex tw-min-w-0 tw-flex-col tw-items-start tw-border-0 tw-bg-transparent tw-p-0 tw-text-left"><h1 className="tw-mb-0 tw-w-full tw-truncate tw-text-sm tw-font-semibold tw-tracking-tight tw-text-white/95 lg:tw-text-xl">{wave.name}</h1><span className="tw-mt-0.5 tw-block tw-w-full tw-truncate tw-text-xs tw-font-normal tw-text-iron-400">{previewText}</span></WaveDescriptionPopover> : <h1 className="tw-mb-0 tw-truncate tw-text-sm tw-font-semibold tw-tracking-tight tw-text-white/95 lg:tw-text-xl">{wave.name}</h1>;
    $[40] = previewText;
    $[41] = showDescriptionPreview;
    $[42] = wave;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] !== t13 || $[45] !== t17 || $[46] !== t18) {
    t19 = <div className="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-x-3">{t13}{t17}{t18}</div>;
    $[44] = t13;
    $[45] = t17;
    $[46] = t18;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  let t20;
  if ($[48] !== isCompact || $[49] !== wave) {
    t20 = !isCompact && <MyStreamWaveTabsMemeSubmit handleMemesSubmit={handleMemesSubmit} wave={wave} />;
    $[48] = isCompact;
    $[49] = wave;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  let t21;
  if ($[51] !== handleWaveLinkActionClick || $[52] !== renderWaveLinkActionIcon || $[53] !== showShareAction || $[54] !== waveLinkActionIconColor || $[55] !== waveLinkActionLabel || $[56] !== waveLinkActionMode) {
    t21 = showShareAction && <button type="button" onClick={handleWaveLinkActionClick} aria-label={waveLinkActionLabel} title={waveLinkActionLabel} data-wave-link-action-mode={waveLinkActionMode} className={`tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-transition tw-duration-150 hover:tw-border-iron-500 hover:tw-bg-iron-800 hover:tw-text-white ${waveLinkActionIconColor}`}>{renderWaveLinkActionIcon()}</button>;
    $[51] = handleWaveLinkActionClick;
    $[52] = renderWaveLinkActionIcon;
    $[53] = showShareAction;
    $[54] = waveLinkActionIconColor;
    $[55] = waveLinkActionLabel;
    $[56] = waveLinkActionMode;
    $[57] = t21;
  } else {
    t21 = $[57];
  }
  let t22;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = () => setIsSearchOpen(true);
    $[58] = t22;
  } else {
    t22 = $[58];
  }
  let t23;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <button type="button" onClick={t22} aria-label="Search messages in this wave" className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-text-iron-200 tw-transition tw-duration-150 hover:tw-border-iron-500 hover:tw-bg-iron-800 hover:tw-text-white"><MagnifyingGlassIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" /></button>;
    $[59] = t23;
  } else {
    t23 = $[59];
  }
  const t24 = `tw-h-4 tw-w-4 tw-flex-shrink-0 tw-text-iron-200 tw-transition tw-duration-300 ${isRightSidebarOpen ? "tw-rotate-180 desktop-hover:group-hover:tw-translate-x-0.5" : "tw-rotate-0 desktop-hover:group-hover:-tw-translate-x-0.5"}`;
  let t25;
  if ($[60] !== t24) {
    t25 = <ChevronDoubleLeftIcon strokeWidth={2} className={t24} />;
    $[60] = t24;
    $[61] = t25;
  } else {
    t25 = $[61];
  }
  let t26;
  if ($[62] !== t25 || $[63] !== toggleRightSidebar) {
    t26 = <button type="button" onClick={toggleRightSidebar} className="tw-group tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-700 tw-shadow-[0_12px_28px_rgba(0,0,0,0.35)] tw-backdrop-blur-sm tw-transition tw-duration-300 tw-ease-out desktop-hover:hover:tw-border-iron-500/80 desktop-hover:hover:tw-bg-iron-700/85 desktop-hover:hover:tw-shadow-[0_16px_34px_rgba(0,0,0,0.4)]" aria-label="Toggle right sidebar">{t25}</button>;
    $[62] = t25;
    $[63] = toggleRightSidebar;
    $[64] = t26;
  } else {
    t26 = $[64];
  }
  let t27;
  if ($[65] !== t20 || $[66] !== t21 || $[67] !== t26) {
    t27 = <div className="tw-flex tw-flex-shrink-0 tw-items-center tw-gap-x-2 md:tw-shrink-0">{t20}{t21}{t23}{t26}</div>;
    $[65] = t20;
    $[66] = t21;
    $[67] = t26;
    $[68] = t27;
  } else {
    t27 = $[68];
  }
  let t28;
  if ($[69] !== t19 || $[70] !== t27) {
    t28 = <div className="tw-flex tw-items-center tw-justify-between tw-gap-x-4 tw-overflow-x-hidden tw-px-2 tw-py-3 sm:tw-px-4">{t19}{t27}</div>;
    $[69] = t19;
    $[70] = t27;
    $[71] = t28;
  } else {
    t28 = $[71];
  }
  let t29;
  if ($[72] !== activeContentTab || $[73] !== setActiveContentTab || $[74] !== wave) {
    t29 = <MyStreamWaveDesktopTabs activeTab={activeContentTab} wave={wave} setActiveTab={setActiveContentTab} />;
    $[72] = activeContentTab;
    $[73] = setActiveContentTab;
    $[74] = wave;
    $[75] = t29;
  } else {
    t29 = $[75];
  }
  let t30;
  if ($[76] !== isMemesWave || $[77] !== isRankWave || $[78] !== nextDecisionTime || $[79] !== timeLeft) {
    t30 = (isMemesWave || isRankWave) && typeof nextDecisionTime === "number" && <div className="tw-flex-shrink-0 tw-px-2 sm:tw-px-4"><CompactTimeCountdown timeLeft={timeLeft} /></div>;
    $[76] = isMemesWave;
    $[77] = isRankWave;
    $[78] = nextDecisionTime;
    $[79] = timeLeft;
    $[80] = t30;
  } else {
    t30 = $[80];
  }
  let t31;
  if ($[81] !== t29 || $[82] !== t30) {
    t31 = <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-border-x-0 tw-border-y tw-border-solid tw-border-iron-800">{t29}{t30}</div>;
    $[81] = t29;
    $[82] = t30;
    $[83] = t31;
  } else {
    t31 = $[83];
  }
  let t32;
  if ($[84] !== t28 || $[85] !== t31) {
    t32 = <div className="tw-flex tw-w-full tw-flex-col tw-bg-iron-950">{t28}{t31}</div>;
    $[84] = t28;
    $[85] = t31;
    $[86] = t32;
  } else {
    t32 = $[86];
  }
  let t33;
  if ($[87] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = () => setIsMemesModalOpen(false);
    $[87] = t33;
  } else {
    t33 = $[87];
  }
  let t34;
  if ($[88] !== isMemesModalOpen || $[89] !== wave) {
    t34 = <MemesArtSubmissionModal isOpen={isMemesModalOpen} wave={wave} onClose={t33} />;
    $[88] = isMemesModalOpen;
    $[89] = wave;
    $[90] = t34;
  } else {
    t34 = $[90];
  }
  let t35;
  if ($[91] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = () => setIsSearchOpen(false);
    $[91] = t35;
  } else {
    t35 = $[91];
  }
  let t36;
  if ($[92] !== handleSearchSelect || $[93] !== isSearchOpen || $[94] !== wave) {
    t36 = <WaveDropsSearchModal isOpen={isSearchOpen} onClose={t35} wave={wave} onSelectSerialNo={handleSearchSelect} />;
    $[92] = handleSearchSelect;
    $[93] = isSearchOpen;
    $[94] = wave;
    $[95] = t36;
  } else {
    t36 = $[95];
  }
  let t37;
  if ($[96] !== t32 || $[97] !== t34 || $[98] !== t36) {
    t37 = <>{t32}{t34}{t36}</>;
    $[96] = t32;
    $[97] = t34;
    $[98] = t36;
    $[99] = t37;
  } else {
    t37 = $[99];
  }
  return t37;
};
export default MyStreamWaveTabsMeme;
function _temp(decision) {
  return {
    decision_time: decision.timestamp
  };
}
function _temp2(decision_1) {
  return decision_1.timestamp > Time.currentMillis();
}
function _temp3(c) {
  return {
    pfp: c.contributor_pfp,
    identity: c.contributor_identity
  };
}